import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ModalBuilder,
  StepperSuccessModal,
  ZnStepperWizard,
  ZnWizardHost,
} from '@zendra/shared';
import { finalize as finalizeObservable } from 'rxjs';

import type { IStepSelectStandsToFuseData } from './interfaces/IStepSelectStandsToFuseData';
import type { IStepDescriptionAndCompletionData } from './interfaces/IStepDescriptionAndCompletionData';
import {
  StandFusionFinalizeRequest,
  StandFusionPlanningService,
} from './services/stand-fusion-planning.service';
import { COMPLETE_STEPPER_MESSAGE, STANDFUSION_CONFIG } from './stand-fusion.config';
import type { IStepCreateAreaMaintenanceData } from './interfaces/IStepCreateAreaMaintenanceData';
import { StepCreateAreaMaintenance } from './step-create-area-maintenance/step-create-area-maintenance';
import { StepDescriptionAndCompletion } from './step-description-and-completion/step-description-and-completion';
import { StepSelectEstablishment } from './step-select-establishment/step-select-establishment';
import { StepSelectStandsToFuse } from './step-select-stands-to-fuse/step-select-stands-to-fuse';

const STEP_COMPONENTS = [
  { id: 1, titleKey: 'StepSelectEstablishment.Title', component: StepSelectEstablishment },
  { id: 2, titleKey: 'StepSelectStandsToFuse.Title', component: StepSelectStandsToFuse },
  { id: 3, titleKey: 'StepCreateAreaMaintenance.Title', component: StepCreateAreaMaintenance },
  {
    id: 4,
    titleKey: 'StepDescriptionAndCompletion.Title',
    component: StepDescriptionAndCompletion,
  },
] as const;

@Component({
  selector: 'lib-stand-fusion',
  standalone: true,
  imports: [ZnStepperWizard, StepperSuccessModal],
  templateUrl: './stand-fusion.html',
})
export class StandFusion extends ZnWizardHost {
  private readonly standFusionModalBuilder = inject(ModalBuilder);
  private readonly standFusionDestroyRef = inject(DestroyRef);
  private readonly standFusionRouter = inject(Router);
  private readonly standFusionPlanningService = inject(StandFusionPlanningService);

  protected readonly wizard = viewChild(ZnStepperWizard);
  private readonly finalizationInProgress = signal(false);

  constructor() {
    super();
    this.initializeStepperWizard(
      STANDFUSION_CONFIG.localizationKey,
      STEP_COMPONENTS,
      COMPLETE_STEPPER_MESSAGE,
      'Geographic::StandFusion.SuccessMessage',
    );
  }

  protected getWizard(): ZnStepperWizard | undefined {
    return this.wizard();
  }

  protected override onSubmitWizard(data: Record<number, unknown>): void {
    if (this.finalizationInProgress()) {
      return;
    }

    const finalizationInput = this.buildFinalizationInput(data);
    if (!finalizationInput) {
      return;
    }

    const modalRef = this.standFusionModalBuilder.openModal(
      COMPLETE_STEPPER_MESSAGE.component,
      COMPLETE_STEPPER_MESSAGE,
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.standFusionDestroyRef))
      .subscribe((confirm: boolean) => {
        if (!confirm || this.finalizationInProgress()) {
          return;
        }

        this.finalizationInProgress.set(true);
        this.standFusionPlanningService
          .finalize(finalizationInput)
          .pipe(
            takeUntilDestroyed(this.standFusionDestroyRef),
            finalizeObservable(() => this.finalizationInProgress.set(false)),
          )
          .subscribe({
            next: () => {
              this.successModalVisible.set(true);
            },
            error: error => {
              console.error(error);
            },
          });
      });
  }

  protected override async onSuccessModalVisibleChange(isVisible: boolean): Promise<void> {
    this.successModalVisible.set(isVisible);
    if (!isVisible) {
      await this.standFusionRouter.navigateByUrl('/geographic/stand');
    }
  }

  private buildFinalizationInput(
    data: Record<number, unknown>,
  ): StandFusionFinalizeRequest | null {
    const step2 = data[2] as IStepSelectStandsToFuseData | undefined;
    const step3 = data[3] as IStepCreateAreaMaintenanceData | undefined;
    const step4 = data[4] as IStepDescriptionAndCompletionData | undefined;

    const persistedStand = step2?.selectedStands.find(row => row.isPersistedStand);
    const standIdsToDeactivate =
      step2?.selectedStands
        .filter(row => !row.isPersistedStand)
        .map(row => row.backendId) ?? [];
    const maintenance = step3?.maintenance;
    const periodId = this.toPositiveInteger(maintenance?.period);
    const adjustmentReasonId = this.toPositiveInteger(maintenance?.reason);
    const standAreaMaintenanceTypeId = this.toPositiveInteger(maintenance?.adjustmentType);
    const description = step4?.transformationReason?.trim() ?? '';

    if (
      !persistedStand ||
      standIdsToDeactivate.length === 0 ||
      !periodId ||
      !adjustmentReasonId ||
      !standAreaMaintenanceTypeId ||
      !description
    ) {
      return null;
    }

    return {
      persistedStandId: persistedStand.backendId,
      standIdsToDeactivate,
      areaMaintenance: {
        periodId,
        adjustmentReasonId,
        standAreaMaintenanceTypeId,
        notes: maintenance?.details?.trim() || null,
      },
      description,
    };
  }

  private toPositiveInteger(value: string | number | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }
}

